import * as React from 'react'
import { Popup } from '@/ui/overlays/Popup'
import { useChartDisc, useUpdateDisc, useDeleteDisc } from '@/lib/api/company/chartScan'
import { StepReview } from './StepReview'
import { useChartT } from './i18n'

type Props = {
  companyId: string
  // DriverTachographChartId of the chart behind an analog activity.
  discId: number | null
  isOpen: boolean
  onClose: () => void
  // Called after a successful save/delete so the caller can refetch activities.
  onSaved?: () => void
}

// "Pokaż tarczkę" modal: opens the full chart editor (same form as the chart
// upload flow) for a single chart loaded by its disc id. Editing + save reuse
// the existing chart-scan PUT endpoint via StepReview/ChartEditorLayout.
export const ShowChartModal = ({ companyId, discId, isOpen, onClose, onSaved }: Props) => {
  const T = useChartT()
  const { data: detail, isLoading } = useChartDisc(companyId, isOpen ? discId : null)
  const scanId = detail?.scanId ?? -1
  const updateDisc = useUpdateDisc(companyId, scanId)
  const deleteDisc = useDeleteDisc(companyId, scanId)

  const handleFinish = () => {
    onClose()
  }

  return (
    <Popup
      open={isOpen}
      onClose={onClose}
      contentClassName="tw-w-[95vw] tw-max-w-[1800px]"
      closeOnBackdrop={false}
      // The drivers area has ad-hoc high overlays (sidebar tw-z-[70]/tw-z-[200]) that
      // sit above the default `modal` layer (z 50), so they bled over the editor.
      // Replicate the confirm dialog's exact, proven config (it renders above those):
      // portal into the `confirm` layer + the same z. Stays interaction-correct since
      // any real confirm dialog renders later in the same container and so above this.
      layer="confirm"
      zIndexClassName="tw-z-[1010]"
    >
      {isLoading && (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-p-12">
          <div className="tw-h-10 tw-w-10 tw-animate-spin tw-rounded-full tw-border-4 tw-border-slate-200 tw-border-t-blue-600" />
          <p className="tw-text-sm tw-text-slate-700">
            {T('loadingDisc', 'Wczytywanie tarczki...')}
          </p>
        </div>
      )}

      {!isLoading && !detail && (
        <div className="tw-p-6 tw-text-center">
          <p className="tw-text-slate-700">{T('discNotFound', 'Nie znaleziono tarczki.')}</p>
          <button
            className="tw-mt-4 tw-rounded tw-bg-slate-200 tw-px-4 tw-py-2"
            onClick={onClose}
          >
            {T('close', 'Zamknij')}
          </button>
        </div>
      )}

      {!isLoading && detail && (
        <StepReview
          companyId={companyId}
          scan={detail}
          onSaveDisc={async (id, payload) => {
            const resp = await updateDisc.mutateAsync({ discId: id, payload })
            if (resp.saved) onSaved?.()
            return resp
          }}
          onDeleteDisc={async (id) => {
            await deleteDisc.mutateAsync(id)
            onSaved?.()
          }}
          onFinish={handleFinish}
        />
      )}
    </Popup>
  )
}
